import React, { useState } from "react";
import * as SubframeCore from "@subframe/core";
import { Button } from "subframe/components/Button";
import { Dialog } from "@mui/material";
import { Skeleton } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import RecognitionPost from "../../../components/SendRecognition/SendRecognition";
import Recognitions from "../../../components/Recognitions/Recognitions";
import { fetchEmployeeRecognitions } from "../../../services/retention";
import { useNavigate } from "react-router-dom";
import { ERROR_CODE } from "constants/constants";
import { RECOGNITION_TYPE } from "../../../constants/constants";
import endpoints from "../../../constants/endpoints";
import { notify } from "modules/Retention/providers/NotificationProvider";
import { AlertVariant } from "modules/Retention/types";

interface RecognitionStats {
  total: number;
  birthday: number;
  milestones: number;
  performance: number;
}

interface UpcomingCelebration {
  type: string;
  date: string;
}

interface RecognitionsOverviewProps {
  stats: RecognitionStats;
  upcomingCelebrations: UpcomingCelebration[];
  onSendRecognitions: () => void;
  user?: any;
  employee?: any;
  isFetchingUser?: boolean;
  isFetchingEmployee?: boolean;
  handleRecognitionPostSubmit?: (employeeId: string) => void;
}

export const RecognitionsOverview: React.FC<RecognitionsOverviewProps> = ({
  stats,
  upcomingCelebrations,
  user = {},
  employee = {},
  isFetchingUser = false,
  handleRecognitionPostSubmit = () => {},
}) => {
  const queryClient = useQueryClient();
  const [isSendRecognitionDialogOpen, setIsSendRecognitionDialogOpen] = useState(false);
  const [isRecognitionsDialogOpen, setIsRecognitionsDialogOpen] = useState(false);
  const [recognitionType, setRecognitionType] = useState(RECOGNITION_TYPE.RECEIVED);

  const handleSendRecognitionClick = () => {
    setIsSendRecognitionDialogOpen(true);
  };

  const { data: recognitions, isLoading: areRecognitionsLoading } = useQuery({
    queryKey: [endpoints.employeeRecognitions, employee?.profile?.employee_id],
    queryFn: () => fetchEmployeeRecognitions({ employee_id: employee?.profile?.employee_id }),
    select: (res) => res?.result?.data,
    enabled: !!employee?.profile?.employee_id,
  });



  const handleRecognitionSubmit = () => {
    handleRecognitionPostSubmit(employee?.profile?.employee_id);
    setIsSendRecognitionDialogOpen(false);
    queryClient.invalidateQueries({
      queryKey: [endpoints.employeeRecognitions, employee?.profile?.employee_id]
    });
    notify({
      title: "Recognition sent successfully.",
      variant : AlertVariant.SUCCESS,
    });
  };

  const handleViewRecognitions = (type: string) => {
    setRecognitionType(type);
    setIsRecognitionsDialogOpen(true);
  };

  return (
    <>
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 sm:gap-8 self-stretch rounded-lg bg-white px-3 sm:px-4 py-4">
              <div className="flex w-full flex-col lg:flex-row items-start gap-6 sm:gap-8">
                <div className="flex w-full lg:w-auto flex-col items-center gap-4 sm:gap-6">
                  <div className="flex w-full items-center justify-between">
                    <span className="line-clamp-1 text-subheader font-subheader text-default-font">
                      Recognitions
                    </span>
                  </div>
                  <div className="flex w-full items-center justify-center gap-8 sm:gap-12">
                    <div 
                      className="flex w-24 sm:w-28 flex-none flex-col items-center cursor-pointer hover:bg-neutral-50 rounded-lg p-2 transition-colors"
                      onClick={() => handleViewRecognitions(RECOGNITION_TYPE.RECEIVED)}
                    >
                      <span className="line-clamp-1 text-section-header font-section-header text-default-font">
                        {areRecognitionsLoading ? (
                          <Skeleton variant="text" width={20} height={16} />
                        ) : (
                          recognitions?.recognitions_received?.length || 0
                        )}
                      </span>
                      <span className="line-clamp-1 text-label font-label text-default-font text-center">
                        Received
                      </span>
                    </div>
                    <div 
                      className="flex w-24 sm:w-28 flex-none flex-col items-center cursor-pointer hover:bg-neutral-50 rounded-lg p-2 transition-colors"
                      onClick={() => handleViewRecognitions(RECOGNITION_TYPE.PROVIDED)}
                    >
                      <span className="line-clamp-1 text-section-header font-section-header text-default-font">
                        {areRecognitionsLoading ? (
                          <Skeleton variant="text" width={20} height={16} />
                        ) : (
                          recognitions?.recognitions_sent?.length || 0
                        )}
                      </span>
                      <span className="line-clamp-1 text-label font-label text-default-font text-center">
                        Sent
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="neutral-primary"
                    onClick={handleSendRecognitionClick}
                    className="w-full sm:w-auto"
                  >
                    Send recognitions
                  </Button>
                </div>
                <div className="hidden lg:flex w-px flex-none flex-col items-center gap-2 self-stretch bg-neutral-border" />
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 sm:gap-6">
                  <div className="flex items-center gap-2">
                    <SubframeCore.Icon
                      className="text-body font-body text-default-font"
                      name="FeatherGift"
                    />
                    <span className="text-body-bold font-body-bold text-default-font">
                      Upcoming Celebrations
                    </span>
                  </div>
                  <div className="flex w-full flex-col items-start gap-2">
                    {upcomingCelebrations.map((celebration, index) => (
                      <div key={index} className="flex w-full flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-6">
                        <span className="text-body font-body text-default-font">
                          {celebration.type}
                        </span>
                        <span className="text-label font-label text-default-font">
                          N/A
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={isSendRecognitionDialogOpen}
        onClose={() => setIsSendRecognitionDialogOpen(false)}
      >
        <RecognitionPost
          user={user}
          canMinimize={true}
          isLoadingUser={isFetchingUser}
          closeButtonIcon="FeatherX"
          onSubmit={handleRecognitionSubmit}
          employeeId={employee?.profile?.employee_id}
          onMinimize={() => setIsSendRecognitionDialogOpen(false)}
        />
      </Dialog>
      <Dialog
        open={isRecognitionsDialogOpen}
        onClose={() => setIsRecognitionsDialogOpen(false)}
      >
        <Recognitions
          type={recognitionType}
          provided={recognitions?.recognitions_sent}
          received={recognitions?.recognitions_received}
          onClose={() => setIsRecognitionsDialogOpen(false)}
        />
      </Dialog>
    </>
  );
}; 